import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { mockData } from '../data/mock';
import SEO from '../components/SEO';
import { getFullUrl } from '../utils/url';

// Lien Calendly du cabinet (page de réservation "Prise de contact")
const CALENDLY_URL = 'https://calendly.com/mmdconseil69/prise-de-contact';

const Contact = () => {
  const [activeTab, setActiveTab] = useState('rdv');
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    entreprise: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Message envoyé avec succès ! Nous vous recontacterons rapidement.');
        setFormData({ nom: '', email: '', telephone: '', entreprise: '', message: '' });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur lors de l\'envoi du contact:', error);
      }
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Charger le script Calendly (widget inline)
  useEffect(() => {
    const scriptSrc = 'https://assets.calendly.com/assets/external/widget.js';
    if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Contact Expert-Comptable Lyon | MMD CONSEIL Rillieux-la-Pape - Devis Gratuit"
        description="Contactez MMD CONSEIL, cabinet d'expertise comptable à Rillieux-la-Pape près de Lyon. Devis gratuit, réponse sous 24h. Tél: 04 82 53 06 84."
        keywords="contact expert-comptable Lyon, devis comptabilité gratuit, cabinet comptable Rillieux-la-Pape, rendez-vous expert comptable, MMD CONSEIL contact"
        url={getFullUrl("/contact")}
      />
      
      {/* Hero Section */}
      <section className="hero-gradient py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-white text-primary-blue px-4 py-2">
              Contactez-nous
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-text-dark mb-6">
              Parlons de Votre Projet
            </h1>
            <p className="text-xl text-text-gray leading-relaxed">
              Notre équipe est à votre disposition pour échanger sur vos besoins 
              et vous proposer un accompagnement sur mesure.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Onglets : Prise de RDV Calendly / Formulaire */}
            <div>
              <div className="flex gap-2 mb-8 bg-light-blue/20 p-1.5 rounded-full w-full max-w-md">
                <button
                  type="button"
                  onClick={() => setActiveTab('rdv')}
                  data-testid="contact-tab-rdv"
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 ${activeTab === 'rdv' ? 'bg-primary-blue text-white' : 'text-text-gray hover:text-primary-blue'}`}
                >
                  <Calendar className="w-4 h-4" />
                  Prendre rendez-vous
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('message')}
                  data-testid="contact-tab-message"
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 ${activeTab === 'message' ? 'bg-primary-blue text-white' : 'text-text-gray hover:text-primary-blue'}`}
                >
                  <Send className="w-4 h-4" />
                  Nous écrire
                </button>
              </div>

              {/* Panneau Prise de rendez-vous (Calendly) */}
              <div className={activeTab === 'rdv' ? '' : 'hidden'} data-testid="calendly-panel">
                <h2 className="text-3xl font-bold text-text-dark mb-3">
                  Réservez un créneau
                </h2>
                <p className="text-text-gray mb-6">
                  Choisissez directement le moment qui vous convient pour un premier échange gratuit et sans engagement.
                </p>
                <div
                  className="calendly-inline-widget rounded-2xl overflow-hidden border border-border-soft"
                  data-url={CALENDLY_URL}
                  style={{ minWidth: '320px', height: '700px' }}
                />
              </div>

              {/* Panneau Formulaire */}
              <div className={activeTab === 'message' ? '' : 'hidden'}>
              <h2 className="text-3xl font-bold text-text-dark mb-6">
                Envoyez-nous un Message
              </h2>
              <p className="text-text-gray mb-8">
                Remplissez ce formulaire et nous vous recontacterons dans les plus brefs délais.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nom" className="text-sm font-medium text-text-dark mb-2 block">
                      Nom complet *
                    </Label>
                    <Input
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Votre nom complet"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-text-dark mb-2 block">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="votre@email.fr"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="telephone" className="text-sm font-medium text-text-dark mb-2 block">
                      Téléphone
                    </Label>
                    <Input
                      id="telephone"
                      name="telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div>
                    <Label htmlFor="entreprise" className="text-sm font-medium text-text-dark mb-2 block">
                      Entreprise
                    </Label>
                    <Input
                      id="entreprise"
                      name="entreprise"
                      value={formData.entreprise}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Nom de votre entreprise"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-medium text-text-dark mb-2 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="form-input resize-none"
                    placeholder="Décrivez votre projet ou vos besoins..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary py-3 text-lg rounded-xl"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="loading-pulse">Envoi en cours...</div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Send className="w-5 h-5" />
                      <span>Envoyer le message</span>
                    </div>
                  )}
                </Button>
              </form>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-text-dark mb-6">
                  Notre Bureau
                </h2>
                <p className="text-text-gray mb-8">
                  MMD CONSEIL vous accueille dans son bureau à Lyon pour vous accompagner dans tous vos projets.
                </p>
              </div>

              {/* Bureaux */}
              {mockData.cabinet.bureaux.map((bureau) => (
                <Card key={bureau.id} className="bg-gradient-to-br from-light-blue to-white border-2 border-accent-blue">
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-primary-blue mb-1">{bureau.nom}</h3>
                      <p className="text-sm text-accent-blue font-medium">{bureau.pays}</p>
                    </div>

                    <div className="space-y-4">
                      {/* Adresse */}
                      <div className="flex items-start space-x-3">
                        <MapPin className="w-5 h-5 text-primary-blue mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-text-dark mb-2">{bureau.adresse}</p>
                          <a 
                            href={bureau.googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-1 text-primary-blue hover:text-secondary-blue transition-colors text-sm font-medium"
                          >
                            <span>Voir sur Google Maps</span>
                          </a>
                        </div>
                      </div>

                      {/* Téléphone */}
                      <div className="flex items-start space-x-3">
                        <Phone className="w-5 h-5 text-primary-blue mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-text-dark font-medium">{bureau.telephone}</p>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex items-start space-x-3">
                        <Mail className="w-5 h-5 text-primary-blue mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-text-dark">{bureau.email}</p>
                        </div>
                      </div>

                      {/* Horaires */}
                      <div className="flex items-start space-x-3">
                        <Clock className="w-5 h-5 text-primary-blue mt-1 flex-shrink-0" />
                        <div className="text-text-dark text-sm space-y-1">
                          <p>{bureau.horaires.semaine}</p>
                          <p>{bureau.horaires.samedi}</p>
                          <p>{bureau.horaires.dimanche}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* WhatsApp - Bureau Lyon uniquement */}
              <Card className="bg-green-50 border-2 border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-text-dark mb-2">Contact WhatsApp (Lyon)</h3>
                      <a
                        href={`https://wa.me/${mockData.cabinet.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-green-600 hover:text-green-700 transition-colors duration-200 font-medium"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>Discuter sur WhatsApp</span>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-spacing bg-light-gray">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-text-dark mb-4">
              Nous Trouver
            </h2>
            <p className="text-xl text-text-gray">
              Notre cabinet est situé à Rillieux-la-Pape, facilement accessible
            </p>
          </div>

          <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
            <iframe
              title="Localisation MMD CONSEIL - 220 Avenue Victor Hugo, 69140 Rillieux-la-Pape"
              src="https://www.google.com/maps?q=220%20Avenue%20Victor%20Hugo%2C%2069140%20Rillieux-la-Pape&output=embed"
              className="w-full h-[380px] md:h-[450px] block"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="contact-google-map"
            />
            <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-6 h-6 text-primary-blue mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-primary-blue">MMD CONSEIL</h3>
                  <p className="text-text-gray text-sm">{mockData.cabinet.adresse}</p>
                </div>
              </div>
              <Button
                asChild
                className="btn-primary px-6 py-3 rounded-lg font-medium"
              >
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=220+Avenue+Victor+Hugo,+69140+Rillieux-la-Pape"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2"
                  data-testid="contact-map-directions"
                >
                  <MapPin className="w-5 h-5" />
                  <span>Itinéraire</span>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;